import React, { useCallback } from 'react'
import { GetProfilesSubscription } from '../generated/urql.client'
import { isIdentity } from '../lib/identity'
import { Blue300, Red800 } from '../lib/colors'
import ReactSelect, { components } from 'react-select'
import { MdPerson } from 'react-icons/md'
import { useI18n } from '../provider/i18n'
import { useAtomValue } from 'jotai'
import { profilesSubscriptionAtom } from '../store/subscription.atoms'

type Profile = GetProfilesSubscription['client_profile'][number]

const ProfileSelect: React.FC<{
  onChange: (profile?: Profile) => void
  value?: Profile
  filter?: (profile: Profile) => boolean
  appearance?: 'input' | 'button'
  className?: string
}> = ({ onChange, filter, value, appearance = 'button', className }) => {
  const i18n = useI18n()
  const [{ data: profilesData }] = useAtomValue(
    profilesSubscriptionAtom
  )

  const renderIdentity = useCallback(
    (identity: unknown) => {
      if (!isIdentity(identity)) {
        return '?'
      }

      switch (identity.type) {
        case 'id_card':
          return i18n.t('components.profileSelect.identity.id_card', {
            value: identity.value,
            countryCode: identity.countryCode,
          })
        case 'passport':
          return i18n.t('components.profileSelect.identity.passport', {
            value: identity.value,
            countryCode: identity.countryCode,
          })
        case 'pesel':
          return i18n.t('components.profileSelect.identity.pesel', {
            value: identity.value,
          })
      }
    },
    [i18n]
  )

  const profiles = profilesData?.client_profile.filter(filter || (() => true))

  return (
    <ReactSelect
      className={className}
      classNames={{
        control: ({ hasValue }) => {
          return appearance === 'button'
            ? `${
                hasValue ? '!border-green-700' : '!border-red-700'
              } !border-2 !rounded-xl h-12 !shadow-none !cursor-pointer`
            : '!shadow-none !rounded-none !border-t-0 !border-l-0 !border-r-0 !border-b !border-b-border !border-solid !border-opacity-50 !p-2.5 !bg-transparent !text-lg'
        },
        singleValue: () =>
          '!text-blue-700 font-bold !whitespace-normal leading-none',
        valueContainer: () => '!flex !flex-row-reverse',
        indicatorSeparator: () => 'hidden',
        dropdownIndicator: () => '!text-blue-600',
        option: ({ isFocused, isSelected }) =>
          `!text-blue-700 ${isFocused ? '!bg-blue-200' : ''} ${
            isSelected ? 'font-bold !bg-white' : ''
          }`,
        menu: () => '!w-auto',
        input: () => '',
        placeholder: ({ hasValue }) =>
          hasValue ? '!text-blue-700' : '!font-bold !text-red-800',
      }}
      noOptionsMessage={() => i18n.t('components.profileSelect.noOptions')}
      loadingMessage={() => i18n.t('components.profileSelect.loading')}
      isOptionSelected={(option, selectValue) =>
        !!selectValue.find((o) => o.value.id === option.value.id)
      }
      components={{
        ValueContainer: ({ children, ...props }) => (
          <components.ValueContainer {...props}>
            {children}
            <MdPerson size={24} color={props.hasValue ? Blue300 : Red800} />
          </components.ValueContainer>
        ),
      }}
      placeholder={i18n.t('components.profileSelectorModal.title')}
      value={
        value
          ? {
              label: <div>{value.firstName + ' ' + value.lastName}</div>,
              value: value,
            }
          : null
      }
      onChange={(option) => option && onChange(option.value)}
      options={profiles?.map((profile) => ({
        value: profile,
        label: (
          <div>
            <p>{profile.firstName + ' ' + profile.lastName}</p>
            <p className="text-xs whitespace-nowrap text-black">
              {renderIdentity(profile.identity)}
            </p>
          </div>
        ),
      }))}
      hideSelectedOptions
    />
  )
}

export default ProfileSelect
